import { Slot } from '@radix-ui/react-slot';
import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';
import type { FluidProp, ParseFluidOption } from './_fluid_prop';
import { parseFluid } from './_fluid_prop';
import type { FlexProps } from './flex.component';
import { Flex } from './flex.component';

export interface StackProps {
	children?: ReactNode;
	/**
	 * Merges its props onto its immediate child.
	 */
	asChild?: boolean;
	/**
	 * The distance between adjacent children.
	 */
	gap?: CSSProperties['gap'];
	/**
	 * Sets width or height equal to container's height.
	 */
	fluid?: FluidProp;
	direction?: 'vertical' | 'horizontal';
}

function Stack({ direction = 'vertical', gap, fluid, asChild, ...props }: StackProps, ref: Ref<HTMLDivElement>): JSX.Element {
	const flexDirection: CSSProperties['flexDirection'] = direction === 'vertical' ? 'column' : 'row';

	const { style: unknownStyle, ...rest } = props as unknown as FlexProps<'div'>;
	const style = {
		...(unknownStyle ?? {}),
		...parseFluid({
			variant: fluid,
			direction,
		} as ParseFluidOption),
	};

	if (asChild) {
		return (
			<Slot
				style={{
					display: 'flex',
					flexDirection: flexDirection,
					gap: gap,
					...style,
				}}
				ref={ref}
				{...rest}
			/>
		);
	}

	return <Flex direction={flexDirection} gap={gap} ref={ref} style={style} {...rest} />;
}

/**
 * `<Stack />` is a general purpose container with Cellula semantics that can be
 * used for custom spacing within a stack of items. It supports Cellula tokens
 * to ensure consistency with other Cellula components.
 */
const _Stack = forwardRef(Stack);

export { _Stack as Stack };

Stack.displayName = 'CellulaStack';
